// @boty/ui - RTL CSS utilities
// Helper functions and constants for RTL-aware styling

import type { SxProps, Theme } from '@mui/material/styles';

/**
 * CSS custom properties for directional spacing.
 * Use these in your component styles for RTL-aware layouts.
 *
 * In MUI's sx prop, use logical properties directly:
 * - marginInlineStart instead of marginLeft
 * - paddingInlineEnd instead of paddingRight
 * - insetInlineStart instead of left
 *
 * @example
 * ```tsx
 * // In a styled component or sx prop
 * sx={{
 *   marginInlineStart: 2, // 16px (2 * 8px spacing)
 *   paddingInlineEnd: 1,  // 8px
 * }}
 * ```
 */
export const CSS_LOGICAL_PROPERTIES = {
  // Margin
  marginInlineStart: 'margin-inline-start',
  marginInlineEnd: 'margin-inline-end',
  marginBlockStart: 'margin-block-start',
  marginBlockEnd: 'margin-block-end',

  // Padding
  paddingInlineStart: 'padding-inline-start',
  paddingInlineEnd: 'padding-inline-end',
  paddingBlockStart: 'padding-block-start',
  paddingBlockEnd: 'padding-block-end',

  // Position
  insetInlineStart: 'inset-inline-start',
  insetInlineEnd: 'inset-inline-end',
  insetBlockStart: 'inset-block-start',
  insetBlockEnd: 'inset-block-end',

  // Border
  borderInlineStart: 'border-inline-start',
  borderInlineEnd: 'border-inline-end',
  borderBlockStart: 'border-block-start',
  borderBlockEnd: 'border-block-end',

  // Border Radius
  borderStartStartRadius: 'border-start-start-radius',
  borderStartEndRadius: 'border-start-end-radius',
  borderEndStartRadius: 'border-end-start-radius',
  borderEndEndRadius: 'border-end-end-radius',

  // Text
  textAlign: {
    start: 'start',
    end: 'end',
  },
} as const;

/**
 * RTL-aware animation direction helper.
 * Returns 1 for LTR and -1 for RTL to reverse horizontal animations.
 *
 * @param isRtl - Whether the current direction is RTL
 * @returns 1 for LTR, -1 for RTL
 *
 * @example
 * ```tsx
 * const { isRtl } = useDirection();
 * const direction = getAnimationDirection(isRtl);
 *
 * // Slide-in animation
 * sx={{
 *   '@keyframes slideIn': {
 *     from: { transform: `translateX(${direction * 100}%)` },
 *     to: { transform: 'translateX(0)' },
 *   },
 * }}
 * ```
 */
export function getAnimationDirection(isRtl: boolean): 1 | -1 {
  return isRtl ? -1 : 1;
}

/**
 * Creates RTL-aware slide-in animation keyframes.
 *
 * @param isRtl - Whether the current direction is RTL
 * @param distance - The distance to slide (default: '100%')
 * @returns Keyframe object for use in sx prop
 *
 * @example
 * ```tsx
 * const { isRtl } = useDirection();
 *
 * <Box
 *   sx={{
 *     animation: 'slideInFromStart 0.3s ease-out',
 *     ...createSlideInKeyframes(isRtl),
 *   }}
 * />
 * ```
 */
export function createSlideInKeyframes(
  isRtl: boolean,
  distance: string = '100%'
): SxProps<Theme> {
  const direction = getAnimationDirection(isRtl);

  return {
    '@keyframes slideInFromStart': {
      from: { transform: `translateX(${direction * -1 * parseFloat(distance)}${distance.replace(/[\d.-]/g, '') || '%'})`, opacity: 0 },
      to: { transform: 'translateX(0)', opacity: 1 },
    },
    '@keyframes slideInFromEnd': {
      from: { transform: `translateX(${direction * parseFloat(distance)}${distance.replace(/[\d.-]/g, '') || '%'})`, opacity: 0 },
      to: { transform: 'translateX(0)', opacity: 1 },
    },
    '@keyframes slideOutToStart': {
      from: { transform: 'translateX(0)', opacity: 1 },
      to: { transform: `translateX(${direction * -1 * parseFloat(distance)}${distance.replace(/[\d.-]/g, '') || '%'})`, opacity: 0 },
    },
    '@keyframes slideOutToEnd': {
      from: { transform: 'translateX(0)', opacity: 1 },
      to: { transform: `translateX(${direction * parseFloat(distance)}${distance.replace(/[\d.-]/g, '') || '%'})`, opacity: 0 },
    },
  };
}

/**
 * Helper to create RTL-aware flex alignment.
 * Use this for chat bubbles and similar layouts where alignment
 * should flip based on direction.
 *
 * @param alignment - 'user' or 'bot' alignment
 * @returns SxProps with RTL-aware justifyContent
 *
 * @example
 * ```tsx
 * // User messages: right in LTR, left in RTL
 * <Box sx={getRtlFlexAlignment('user')}>
 *   <UserBubble />
 * </Box>
 *
 * // Bot messages: left in LTR, right in RTL
 * <Box sx={getRtlFlexAlignment('bot')}>
 *   <BotBubble />
 * </Box>
 * ```
 */
export function getRtlFlexAlignment(
  alignment: 'user' | 'bot'
): SxProps<Theme> {
  if (alignment === 'user') {
    // User messages: end in LTR (right), start in RTL (left)
    return {
      display: 'flex',
      justifyContent: 'flex-end',
      '[dir="rtl"] &': {
        justifyContent: 'flex-start',
      },
    };
  }

  // Bot messages: start in LTR (left), end in RTL (right)
  return {
    display: 'flex',
    justifyContent: 'flex-start',
    '[dir="rtl"] &': {
      justifyContent: 'flex-end',
    },
  };
}

/**
 * Documentation for CSS logical properties usage.
 * This is for reference when writing component styles.
 *
 * ALWAYS USE (Logical Properties):
 * - margin-inline-start / margin-inline-end
 * - padding-inline-start / padding-inline-end
 * - inset-inline-start / inset-inline-end
 * - border-inline-start / border-inline-end
 * - text-align: start / text-align: end
 *
 * NEVER USE (Physical Properties):
 * - margin-left / margin-right
 * - padding-left / padding-right
 * - left / right
 * - border-left / border-right
 * - text-align: left / text-align: right
 */
export const RTL_STYLE_GUIDE = `
CSS Logical Properties Reference:

Physical → Logical
==================
margin-left      → margin-inline-start
margin-right     → margin-inline-end
padding-left     → padding-inline-start
padding-right    → padding-inline-end
left             → inset-inline-start
right            → inset-inline-end
border-left      → border-inline-start
border-right     → border-inline-end
text-align: left → text-align: start
text-align: right→ text-align: end

In MUI sx prop:
===============
sx={{ marginLeft: 2 }}      → sx={{ marginInlineStart: 2 }}
sx={{ paddingRight: 2 }}    → sx={{ paddingInlineEnd: 2 }}
sx={{ left: 0 }}            → sx={{ insetInlineStart: 0 }}
sx={{ textAlign: 'left' }}  → sx={{ textAlign: 'start' }}
`;
